import { useCallback, useEffect, useRef, useState } from "react";



/**

 * Glowkit brand wordmark (vanity-mirror mark lives only in favicon / SVG assets).

 * Single text node "Glowkit" so metallic shimmer (background-clip:text) sweeps

 * continuously across letter glyphs only. Playfair italic; pink period stays

 * outside the clipped text as accent. Hover shine yoyos while hovered

 * (`is-shimmering`); mouseleave removes the class to stop the loop.

 * Under prefers-reduced-motion: solid wordmark, no load/hover shimmer.

 * GlowkitMark kept exported for favicon/OG asset reuse if needed later.

 */

export function GlowkitMark({ className = "h-8 w-8", title = "Glowkit" }) {

  return (

    <svg

      xmlns="http://www.w3.org/2000/svg"

      viewBox="0 0 64 64"

      fill="none"

      className={className}

      role="img"

      aria-hidden={title ? undefined : true}

      aria-label={title || undefined}

    >

      <ellipse cx="32" cy="27.5" rx="16" ry="18.5" stroke="currentColor" strokeWidth="2.1" />

      <path

        d="M24.2 16.2c4-3.6 11.2-4 15.4.2"

        stroke="#C99A9A"

        strokeWidth="1.55"

        strokeLinecap="round"

      />

      <path d="M45.2 30.2H35.5" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />

      <path d="M45.2 30.2v5.2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />

      <path d="M32 46.2V52" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />

      <path

        d="M24.8 55.5c2.3-2.1 4.8-3.1 7.2-3.1s4.9 1 7.2 3.1"

        stroke="currentColor"

        strokeWidth="2.1"

        strokeLinecap="round"

      />

    </svg>

  );

}



export default function Logo({ className = "" }) {

  const shimmerRef = useRef(null);

  const [reduceMotion, setReduceMotion] = useState(() =>

    typeof window !== "undefined"

      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches

      : false,

  );



  useEffect(() => {

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");

    const onChange = () => {

      setReduceMotion(media.matches);

      if (media.matches) {

        const el = shimmerRef.current;

        if (!el) return;

        el.classList.remove("brand-shimmer--boot", "is-shimmering");

      }

    };

    onChange();

    media.addEventListener("change", onChange);

    return () => media.removeEventListener("change", onChange);

  }, []);



  const startShineLoop = useCallback(() => {

    if (reduceMotion) return;

    const el = shimmerRef.current;

    if (!el) return;

    el.classList.remove("is-shimmering");

    void el.offsetWidth;

    el.classList.add("is-shimmering");

  }, [reduceMotion]);



  const stopShineLoop = useCallback(() => {

    const el = shimmerRef.current;

    if (!el) return;

    el.classList.remove("is-shimmering");

  }, []);



  useEffect(() => {

    const el = shimmerRef.current;

    if (!el || reduceMotion) return undefined;

    el.classList.add("brand-shimmer--boot");

    const onEnd = (event) => {

      if (event.target !== el) return;

      if (event.animationName === "brand-shimmer-load") {

        el.classList.remove("brand-shimmer--boot");

      }

    };

    el.addEventListener("animationend", onEnd);

    return () => el.removeEventListener("animationend", onEnd);

  }, [reduceMotion]);



  return (

    <span

      className={`group/logo inline-flex items-center text-ink ${className}`}

      aria-label="Glowkit"

      onMouseEnter={startShineLoop}

      onMouseLeave={stopShineLoop}

    >

      <span className="brand-lockup brand-lockup--glowkit inline-flex items-baseline">

        <span

          ref={shimmerRef}

          className={`brand-shimmer text-2xl md:text-3xl leading-none tracking-tight${

            reduceMotion ? " brand-shimmer--static" : ""

          }`}

          aria-hidden="true"

        >

          Glowkit

        </span>

        <span

          className="brand-period text-2xl md:text-3xl leading-none tracking-tight"

          aria-hidden="true"

        >

          .

        </span>

      </span>

    </span>

  );

}

